import React from 'react'
import {SelectedCard} from "./SelectedCard";
import {formattedDateTime} from "../../services/date";
import {getEventCardFooter} from "./EntitySelectorModal.helpers";
import {ENTITY_TYPES} from "../EntitySelector/EntitySelector.constants";
import {type EntityType, type SelectedItem, type SelectedItemsData} from "../EntitySelector/EntitySelector.types";
import {useOperator} from "../../context/OperatorContext";
import {SelectedEntitySection} from "./SelectedEntitySection";

// Types ----

type SelectedItemsProps = {
  selectedItems: SelectedItemsData
  onRemoveItem: <K extends EntityType>(item: SelectedItem<K>, entityType: K) => void
  isMultiSite?: boolean
}

// Component ----

function SelectedItems({ selectedItems, onRemoveItem, isMultiSite = false }: SelectedItemsProps) {
  const {termFor} = useOperator();

  return (
    <>
      {!!selectedItems.achievement.length &&
        <SelectedEntitySection heading={termFor('Achievements')}>
          {selectedItems.achievement.map(achievement => (
            <SelectedCard
              key={`achievement-${achievement.id}`}
              heading={achievement.name}
              footer={achievement.description}
              onRemove={() => onRemoveItem(achievement, ENTITY_TYPES.ACHIEVEMENT)}
              imageUrl={achievement.image?.preview_url}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.article.length &&
        <SelectedEntitySection heading={termFor('Articles')}>
          {selectedItems.article.map(article => (
            <SelectedCard
              key={`article-${article.id}`}
              heading={article.title}
              footer={article.excerpt}
              onRemove={() => onRemoveItem(article, ENTITY_TYPES.ARTICLE)}
              imageUrl={article.image?.preview_url}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.collection.length &&
        <SelectedEntitySection heading={termFor('Collections')}>
          {selectedItems.collection.map(collection => (
            <SelectedCard
              key={`collection-${collection.id}`}
              heading={collection.name}
              footer={collection.location}
              onRemove={() => onRemoveItem(collection, ENTITY_TYPES.COLLECTION)}
              imageUrl={collection.images[0]?.preview_url}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.event.length &&
        <SelectedEntitySection heading={termFor('Events')}>
          {selectedItems.event.map(event => (
            <SelectedCard
              key={`event-${event.id}`}
              heading={event.name}
              subheading={formattedDateTime(event.starts_at)}
              footer={getEventCardFooter(event, isMultiSite)}
              onRemove={() => onRemoveItem(event, ENTITY_TYPES.EVENT)}
              imageUrl={event.image?.preview_url}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.exhibit.length &&
        <SelectedEntitySection heading={termFor('Exhibits')}>
          {selectedItems.exhibit.map(exhibit => (
            <SelectedCard
              key={`exhibit-${exhibit.id}`}
              heading={exhibit.name}
              footer={isMultiSite ? exhibit.zone?.site?.name : null}
              onRemove={() => onRemoveItem(exhibit, ENTITY_TYPES.EXHIBIT)}
              imageUrl={exhibit.image?.preview_url}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.facility.length &&
        <SelectedEntitySection heading={termFor('Facilities')}>
          {selectedItems.facility.map(facility => (
            <SelectedCard
              key={`facility-${facility.id}`}
              heading={facility.name}
              footer={isMultiSite ? facility.zone?.site?.name : null}
              onRemove={() => onRemoveItem(facility, ENTITY_TYPES.FACILITY)}
              imageUrl={facility.image?.preview_url}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.place.length &&
        <SelectedEntitySection heading={termFor('Places')}>
          {selectedItems.place.map(place => (
            <SelectedCard
              key={`place-${place.id}`}
              heading={place.name}
              footer={place.address?.city}
              onRemove={() => onRemoveItem(place, ENTITY_TYPES.PLACE)}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.quiz.length &&
        <SelectedEntitySection heading={termFor('Quizzes')}>
          {selectedItems.quiz.map(quiz => (
            <SelectedCard
              key={`quiz-${quiz.id}`}
              heading={quiz.title}
              footer={quiz.type?.name}
              onRemove={() => onRemoveItem(quiz, ENTITY_TYPES.QUIZ)}
              imageUrl={quiz.image?.preview_url}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.site.length &&
        <SelectedEntitySection heading={termFor('Sites')}>
          {selectedItems.site.map(site => (
            <SelectedCard
              key={`site-${site.id}`}
              heading={site.name}
              footer={site.address?.city}
              onRemove={() => onRemoveItem(site, ENTITY_TYPES.SITE)}
              imageUrl={site.image?.preview_url}
            />
          ))}
        </SelectedEntitySection>
      }

      {!!selectedItems.zone.length &&
        <SelectedEntitySection heading={termFor('Zones')}>
          {selectedItems.zone.map(zone => (
            <SelectedCard
              key={`zone-${zone.id}`}
              heading={zone.name}
              footer={isMultiSite ? zone.site?.name : null}
              onRemove={() => onRemoveItem(zone, ENTITY_TYPES.ZONE)}
            />
          ))}
        </SelectedEntitySection>
      }
    </>
  );
}

export {SelectedItems};